'use client';

import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { getAriaLabel } from '@bridge/utils';
import { DropzoneInput, FormControl, FormDescription, FormItem, FormLabel, FormMessage } from '../../components';
import type { FieldValues, Path } from 'react-hook-form';
import type { BaseComponentProps, BaseFieldProps } from './types';

/** Default max file size: 4.5MB in bytes */
const DEFAULT_MAX_FILE_SIZE = 4.5 * 1024 * 1024;

/**
 * Component props specific to FileField
 * Extends the base props with dropzone-specific options
 */
export type FileComponentProps = BaseComponentProps<typeof DropzoneInput> & {
  /**
   * Accepted file types (e.g., 'image/*', '.pdf,.doc'), used to reject non-matching files
   * with an inline error. Monorail's DeclarativeForm::File widget declares this same shape.
   */
  accept?: string;
  /** Custom label shown in the dropzone */
  dropzoneLabel?: string;
  /** Helper text shown below the dropzone label */
  helperText?: string;
  /**
   * When true, converts the file to a base64 data URL and stores that as the field value.
   * The field value will be a string like "data:image/png;base64,..." instead of a File object.
   */
  convertToBase64?: boolean;
  /**
   * Maximum file size in bytes. Defaults to 4.5MB.
   * Set to `null` or `Infinity` to disable the size limit.
   */
  maxFileSize?: number | null;
  /**
   * Display-only label for a file uploaded in a prior session (e.g. "File already uploaded").
   * Shown whenever the field has no value of its own, without being written to form state —
   * so an untouched field naturally submits nothing rather than echoing back a placeholder.
   */
  existingUploadLabel?: string;
};
interface FileFieldProps<TFieldValues extends FieldValues> extends BaseFieldProps<TFieldValues> {
  componentProps: FileComponentProps;
}

/**
 * FileField renders a drag-and-drop file upload zone.
 *
 * Features:
 * - Drag and drop file support
 * - Click to open file picker
 * - Configurable accepted file types
 * - Visual feedback for drag over, file selected, and error states
 * - Single file upload only
 *
 * @example
 * ```typescript
 * {
 *   type: 'file',
 *   name: 'document',
 *   label: 'Upload document',
 *   componentProps: {
 *     accept: '.pdf,.doc,.docx',
 *     dropzoneLabel: 'Click to upload or drag and drop',
 *     helperText: 'PDF or Word documents up to 10MB',
 *     maxFileSize: 10 * 1024 * 1024, // 10MB (defaults to 4.5MB)
 *   }
 * }
 * ```
 */
/**
 * Converts a File to a base64 data URL string.
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
export function FileField<TFieldValues extends FieldValues>({
  field,
  fieldConfig,
  fieldState,
  componentProps,
  labelSize
}: FileFieldProps<TFieldValues>) {
  const {
    id,
    disabled: componentDisabled,
    accept,
    dropzoneLabel,
    helperText,
    convertToBase64,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    existingUploadLabel,
    ...restComponentProps
  } = componentProps;
  const {
    setValue
  } = useFormContext<TFieldValues>();
  const isDisabled = field.disabled || componentDisabled;
  const fieldId = id || String(fieldConfig.name);
  const descriptionId = fieldConfig.description ? `${fieldId}-description` : undefined;

  // When convertToBase64 is enabled, we need to track the File separately
  // since the field value will be a base64 string
  const [displayFile, setDisplayFile] = useState<File | null>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const [typeError, setTypeError] = useState<string | null>(null);
  const hasError = !!fieldState.error || !!sizeError || !!typeError;
  const errorId = hasError ? `${fieldId}-error` : undefined;
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined;

  // Sync displayFile when field value changes externally (e.g., form reset)
  useEffect(() => {
    // If value is cleared externally, clear the display file and any errors
    if (!field.value) {
      setSizeError(null);
      setTypeError(null);
      if (convertToBase64) {
        setDisplayFile(null);
      }
    }
  }, [convertToBase64, field.value]);
  const handleReject = () => {
    setSizeError(null);
    const accepted = accept?.split(',').map(type => type.trim()).filter(Boolean).join(', ');
    setTypeError(accepted ? `Unsupported file type. Accepted types: ${accepted}` : 'Unsupported file type');
  };
  const handleChange = async (file: File | null) => {
    // Clear any previous errors
    setSizeError(null);
    setTypeError(null);

    // Check file size limit
    if (file && maxFileSize != null && maxFileSize !== Infinity && file.size > maxFileSize) {
      const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(1);
      setSizeError(`File size exceeds the maximum allowed size of ${maxSizeMB}MB`);
      return;
    }
    if (convertToBase64) {
      setDisplayFile(file);
      if (file) {
        const base64 = await fileToBase64(file);
        field.onChange(base64);
      } else {
        // Set to undefined to remove from form data (undefined is omitted in JSON.stringify)
        setValue(fieldConfig.name as Path<TFieldValues>, undefined as TFieldValues[Path<TFieldValues>]);
      }
    } else {
      if (file) {
        field.onChange(file);
      } else {
        // Set to undefined to remove from form data (undefined is omitted in JSON.stringify)
        setValue(fieldConfig.name as Path<TFieldValues>, undefined as TFieldValues[Path<TFieldValues>]);
      }
    }
  };

  // Determine which value to show in the dropzone. `displayFile` only holds a value
  // once the user picks a new file this session — for a field resumed with a
  // previously-uploaded value (base64 string from the server, no File object yet),
  // fall back to showing that persisted string so the field doesn't look empty.
  const placeholderLabel = existingUploadLabel ?? null;
  const displayValue = convertToBase64 ? displayFile ?? (typeof field.value === 'string' && field.value ? field.value : placeholderLabel) : field.value as File | string | null ?? placeholderLabel;
  return <FormItem className="flex-1">
      {fieldConfig.label && <FormLabel htmlFor={fieldId} id={`${fieldId}-label`} required={fieldConfig.required} size={labelSize} weight="medium">
          {fieldConfig.label}
        </FormLabel>}
      <FormControl>
        <DropzoneInput {...restComponentProps} accept={accept} aria-describedby={describedBy} aria-invalid={hasError} aria-label={getAriaLabel(fieldConfig.label)} disabled={isDisabled} helperText={helperText} id={fieldId} label={dropzoneLabel} onBlur={field.onBlur} onChange={handleChange} onReject={handleReject} ref={field.ref} value={displayValue} />
      </FormControl>
      {fieldConfig.description && <FormDescription id={descriptionId}>{fieldConfig.description}</FormDescription>}
      {(fieldState.error?.message || sizeError || typeError) && <FormMessage id={errorId}>{fieldState.error?.message || sizeError || typeError}</FormMessage>}
    </FormItem>;
}